import { useDrop } from 'react-dnd';
import { InspectorDetails } from '../../models/inspector';
import { WorkOrder } from '../../models/workOrder';
import { useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Pagination,
  Popover,
  Select,
  Snackbar,
  Tooltip,
  Typography,
} from '@mui/material';
import { DialogActionsBar } from '@progress/kendo-react-dialogs';
import dayjs from 'dayjs';
import { workOrdersApiKeyStore } from '../../api/workorders-api-key-store';
import { useSaveData } from '../../hooks/useSaveData';
import { useAuthorize } from '../../hooks/use-authorize';
import { UserPermissionName } from '../../models/user-permission-name';
import { LoadingIndicator } from '../../components/loading-indicator.component';
import BulkScheduleCalenderComponent from '../scheduling/bulk-schedule-calender.component';
import { Icons } from '../../assets/icons';

interface InspectorsListProps {
  inspectors: InspectorDetails[];
  setOpenDialog: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedWorkOrder: React.Dispatch<React.SetStateAction<WorkOrder[]>>;
  setSelectedInspector: React.Dispatch<React.SetStateAction<InspectorDetails | null>>;
}

const InspectorsList = ({
  inspectors,
  setOpenDialog,
  setSelectedInspector,
  setSelectedWorkOrder,
}: InspectorsListProps) => {
  if (!inspectors || inspectors.length === 0) return <p style={{ textAlign: 'center' }}>No inspectors available.</p>;

  return (
    <div className="flex flex-col gap-4 p-4" style={{ width: '100%' }}>
      <h3 style={{ fontSize: '20px', fontWeight: 'bold', padding: 0, marginBottom: '10px' }}>INSPECTORS</h3>
      <span className="btnInfoOuterBlock">
        {' '}
        <Typography
          style={{
            position: 'relative',
            cursor: 'pointer',
            color: '#007bff',

            display: 'inline-flex',
            alignItems: 'center',
          }}
        >
          <Tooltip title="Drop the work order under Inspections here to assign it.">
            <span className="btn btn-sm btn-dark btnInfoBlock" style={{}}></span>
          </Tooltip>
        </Typography>
      </span>
      {inspectors.map((inspector) => (
        <DroppableInspector
          key={inspector.userID}
          inspector={inspector}
          setOpenDialog={setOpenDialog}
          setSelectedInspector={setSelectedInspector}
          setSelectedWorkOrder={setSelectedWorkOrder}
        />
      ))}
    </div>
  );
};

const DroppableInspector = ({
  inspector,
  setOpenDialog,
  setSelectedInspector,
  setSelectedWorkOrder,
}: {
  inspector: any;
  setOpenDialog: (value: boolean) => void;
  setSelectedInspector: (inspector: any) => void;
  setSelectedWorkOrder: (workOrder: WorkOrder[]) => void;
}) => {
  const [{ isOver }, drop] = useDrop(() => ({
    accept: 'inspection',
    drop: (item: { inspections: WorkOrder[] }) => {
      setOpenDialog(true);
      setSelectedWorkOrder(item.inspections);
      setSelectedInspector(inspector);
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  }));

  const [anchorEl, setAnchorEl] = useState(null);
  const [inspectorID, setInspectorID] = useState<any>();
  const [calendarDialog, setCalendarDialog] = useState<any>();

  const handlePopoverOpen = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const handleInspectorCalendar = (inspectorID: any) => {
    setInspectorID(inspectorID);
    setCalendarDialog(true);
  };

  return (
    <div
      ref={drop}
      style={{
        background: isOver ? 'white' : '#f0f8ff',
        marginBottom: 10,
        padding: '8px 10px',
        position: 'relative',
        border: isOver ? '1px dashed #000' : '1px solid transparent',
      }}
      className="inspectorBlock bg-white shadow-md rounded-lg p-4 border border-gray-300"
    >
      <h3 className="text-lg text-gray-600" style={{ display: 'flex', justifyContent: 'space-between' }}>
        {inspector?.userName}{' '}
        <Typography
          variant="body1"
          style={{
            position: 'absolute',
            top: '8px',
            right: '95px',
            fontSize: '12px',
            padding: '0px 6px',
          }}
        >
          {/* <strong>Calendar:</strong>{' '} */}
          <IconButton aria-label="move" onClick={() => handleInspectorCalendar(inspector?.userID)}>
            <Tooltip title={<Typography variant="body1">Calendar</Typography>}>
              <img src={Icons.IconCalendar} alt="Schedule" />
            </Tooltip>
          </IconButton>
        </Typography>
      </h3>
      <p className="text-gray-600">
        <small>Inspection Count: </small>{' '}
        <span style={{ color: 'green', fontWeight: 'bold' }}>{inspector?.scheduledInspectionCount}</span>
      </p>
      <button
        onClick={handlePopoverOpen}
        className="btn btn-sm btn-dark"
        style={{
          position: 'absolute',
          top: '15px',
          right: '10px',
          fontSize: '12px',
          padding: '0px 6px',
          cursor: 'pointer',
        }}
      >
        More Info
      </button>
      <Popover
        className="popOverOuterWrap inspectorPopover"
        open={open}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <div style={{ padding: '10px' }} className="popOverInnererWrap">
          <Typography variant="body1">
            <small>Market Area: </small> {inspector?.userMarketAreas || ''}
          </Typography>
          <Typography variant="body1">
            <small>Skill Sets: </small> {inspector?.userSkillsets || ''}
          </Typography>
          <Typography variant="body1">
            <small>Equipments: </small> {inspector?.userEquipments || ''}
          </Typography>
        </div>
      </Popover>
      {calendarDialog && (
        <BulkScheduleCalenderComponent
          open={calendarDialog}
          inspectorID={inspectorID}
          onClosed={() => setCalendarDialog(false)}
        />
      )}
    </div>
  );
};

const Inspectors = ({ inspectors, handleInspectorFilterClick, handleInspectionsFilterClick }: any) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState<WorkOrder[]>([]);
  const [selectedInspector, setSelectedInspector] = useState<InspectorDetails | null>(null);
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState({ severity: 'success', messageInfo: '' });
  const [pendingErrorRules, setPendingErrorRoles] = useState([]);
  const [workOrderForWarnings, setWorkOrderForWarnings] = useState([]);
  const [openDialogForDate, setOpenDialogForDate] = useState(false);
  const ishavePermission = useAuthorize([UserPermissionName?.OVERRIDE]);

  const { isLoading, mutateAsync } = useSaveData<any>(workOrdersApiKeyStore.bulkSchedule());

  const { isLoading: sheduleRuleLoading, mutateAsync: sheduleRuleCheck } = useSaveData<any>(
    workOrdersApiKeyStore.validateWorkOrderSheduleRules()
  );

  const closeDialog = () => {
    setOpenDialog(false);
  };

  const closeValidationDialog = () => {
    setOpenDialogForDate(false);
  };

  const handleSubmitConfirm = async () => {
    setOpenDialog(false);
    if (selectedWorkOrder?.length === 0) {
      setOpen(true);
      setPendingErrorRoles([]);
      setMessage({ severity: 'error', messageInfo: 'Please select a work order' });
      return;
    }

    const data = {
      workOrders: selectedWorkOrder,
      keepScheduledDateasOriginal: true,
      inspectorID: selectedInspector?.userID,
    };
    handleSubmit(data);
  };

  const handleDirectApiCall = async (data: any) => {
    await mutateAsync({
      data: {
        ...data,
        scheduledDate: '',
        workOrders: data.workOrders ? data.workOrders.map((p: any) => p.workOrderID) : [],
        ScheduleFromArea: 'Schedule Balancing',
      },
    });
    setMessage({ severity: 'success', messageInfo: 'Bulk scheduling completed successfully!' });
    setOpen(true);
    setWorkOrderForWarnings([]);
    setPendingErrorRoles([]);
    setOpenDialogForDate(false);
    handleInspectionsFilterClick();
    handleInspectorFilterClick();
  };

  const addPendingRule = (workOrder: any, pendingRulesMessages: any, message: any) => {
    const existingEntry = pendingRulesMessages?.find((ruleMessage: any) => ruleMessage.workOrderName === workOrder);

    if (existingEntry) {
      // If it exists, add the message to its pendingRules
      existingEntry.pendingRules.push(message);
    } else {
      // If it does not exist, add a new entry with the message
      pendingRulesMessages.push({
        workOrderName: workOrder,
        pendingRules: [message],
      });
    }
  };

  const processWarnings = (workOrderForWarnings: any, pendingRulesMessages: any, message: any) => {
    setWorkOrderForWarnings(workOrderForWarnings);

    workOrderForWarnings.forEach((workOrder: any) => {
      addPendingRule(workOrder, pendingRulesMessages, message);
    });

    setPendingErrorRoles(pendingRulesMessages);
    setOpenDialogForDate(true);
  };

  const handleSubmit = async (data: any) => {
    const scheduleMessage = 'The schedule time is greater than cut off time';

    const rulePayload = {
      workOrderInfos: data?.workOrders?.map((item: any) => ({
        workOrderID: item.workOrderID,
        workOrderNumber: item.workOrderNumber,
        scheduledDate:
          item?.workOrderStatus == "Ready" ? item?.proposedScheduledDate :
            (item?.workOrderStatus == "Scheduled" && !item?.requestedRescheduledDate) ? item?.scheduledDate :
              (item?.workOrderStatus == "Scheduled" && item?.requestedRescheduledDate) ? item?.proposedScheduledDate
                : dayjs(data?.scheduledDate)?.format('MM/DD/YYYY'),
      }))
    };

    const ruleResponse: any = await sheduleRuleCheck({ data: rulePayload });

    const pendingRuleResponse = ruleResponse?.data?.map((item: any) => ({
      workOrderName: item.workOrderNumber,
      pendingRules: item.pendingRules,
      preReqWorkOrders: item?.preReqWorkOrders
    }));

    const pendingRulesMessages = pendingRuleResponse;

    if (!selectedInspector) {
      setOpen(true);
      setPendingErrorRoles([]);
      setMessage({ severity: 'error', messageInfo: 'Please select inspector' });
    } else if (data?.keepScheduledDateasOriginal) {
      const proposedScheduleDateWarnings: string[] = [];
      const scheduleDateWarnings: string[] = [];
      const requestScheduleDateWarnings: string[] = [];

      data?.workOrders?.forEach((workOrder: any) => {
        const { workOrderStatus, proposedScheduledDate, scheduledDate, requestedRescheduledDate, workOrderNumber } =
          workOrder;

        // Check for past proposed schedule date
        if (workOrderStatus === 'Ready' && dayjs(proposedScheduledDate).isBefore(dayjs().startOf('day'))) {
          proposedScheduleDateWarnings.push(workOrderNumber);
        }

        // Check for scheduled work orders
        if (workOrderStatus === 'Scheduled') {
          if (!requestedRescheduledDate && dayjs(scheduledDate).isBefore(dayjs().startOf('day'))) {
            scheduleDateWarnings.push(workOrderNumber);
          } else if (requestedRescheduledDate && dayjs(requestedRescheduledDate).isBefore(dayjs().startOf('day'))) {
            requestScheduleDateWarnings.push(workOrderNumber);
          }
        }
      });

      const showErrorMessage = (workOrderNumbers: string[], message: string) => {
        setOpen(true);
        setPendingErrorRoles([]);
        setMessage({
          severity: 'error',
          messageInfo: `Selected work order ${workOrderNumbers.join(', ')} ${message}`,
        });
      };

      if (
        proposedScheduleDateWarnings?.length > 0 ||
        scheduleDateWarnings?.length > 0 ||
        requestScheduleDateWarnings?.length > 0
      ) {
        if (proposedScheduleDateWarnings.length > 0) {
          showErrorMessage(proposedScheduleDateWarnings, 'have past proposed schedule date');
        } else if (scheduleDateWarnings.length > 0) {
          showErrorMessage(scheduleDateWarnings, 'have past schedule date');
        } else if (requestScheduleDateWarnings.length > 0) {
          showErrorMessage(requestScheduleDateWarnings, 'have past requested reschedule date');
        }
      } else {
        // When `keepScheduledDateasOriginal` is true
        const workOrderForWarnings = data?.workOrders
          ?.filter((order: any) => {
            const scheduledDate = dayjs(order.scheduledDate);
            const cutOffTime = order.cutOffTime ? dayjs(order.cutOffTime, 'hh:mm A') : null;
            const proposedScheduledDate = order.proposedScheduledDate ? dayjs(order.proposedScheduledDate) : null;
            const now = dayjs().startOf('minute');

            if (order.workOrderStatus === 'Scheduled' && scheduledDate.isSame(dayjs(), 'day')) {
              return cutOffTime && cutOffTime.isBefore(dayjs());
            } else if (
              order.workOrderStatus === 'Ready' &&
              proposedScheduledDate &&
              proposedScheduledDate.isSame(dayjs(), 'day')
            ) {
              return cutOffTime && cutOffTime.isBefore(dayjs());
            }
            return false;
          })
          .map((order: any) => order.workOrderNumber);

        if (workOrderForWarnings?.length > 0 || pendingRulesMessages?.length > 0) {
          if (workOrderForWarnings?.length > 0) {
            processWarnings(workOrderForWarnings, pendingRulesMessages, scheduleMessage);
          } else {
            setWorkOrderForWarnings([]);
            setPendingErrorRoles(pendingRulesMessages);
            setOpenDialogForDate(true);
          }
        } else {
          handleDirectApiCall(data);
        }
      }
    } else {
      if (pendingRulesMessages?.length > 0) {
        setWorkOrderForWarnings([]);
        setPendingErrorRoles(pendingRulesMessages);
        setOpenDialogForDate(true);
      } else {
        handleDirectApiCall(data);
      }
    }
  };

  const handleCloseSnakbar = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  };

  //   pagination
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);

  const handlePageChange = (event: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
  };

  const handleItemsPerPageChange = (event: any) => {
    setItemsPerPage(event.target.value as number);
    setPage(1);
  };

  const validInspectors = Array.isArray(inspectors) ? inspectors : [];

  const startIndex = (page - 1) * itemsPerPage;
  const paginatedInspections = validInspectors.slice(startIndex, startIndex + itemsPerPage);

  const handleSumbitConfirm = async () => {
    const data = {
      workOrders: selectedWorkOrder,
      keepScheduledDateasOriginal: true,
      inspectorID: selectedInspector?.userID,
    };
    handleDirectApiCall(data);
    setOpenDialogForDate(false);
  };

  const isReadOnlyPermission = useAuthorize([UserPermissionName?.READ_ONLY]);

  return (
    <div>
      {(isLoading || sheduleRuleLoading) && <LoadingIndicator isLoading={isLoading || sheduleRuleLoading} />}
      <InspectorsList
        inspectors={paginatedInspections}
        setOpenDialog={setOpenDialog}
        setSelectedInspector={setSelectedInspector}
        setSelectedWorkOrder={setSelectedWorkOrder}
      />
      {validInspectors.length > 0 && (
        <Grid
          padding={10}
          style={{
            padding: '20px 0px 15px',
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
          }}
        >
          <Pagination
            count={Math.ceil(validInspectors.length / itemsPerPage)}
            page={page}
            onChange={handlePageChange}
            variant="outlined"
            shape="rounded"
          />
          <FormControl className="pagingperSelect" variant="outlined">
            <Select
              labelId="items-per-page-label"
              id="items-per-page-select"
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
            >
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={30}>30</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
            <span>items per page</span>
          </FormControl>
        </Grid>
      )}

      <Snackbar
        open={open}
        autoHideDuration={6000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        onClose={handleCloseSnakbar}
      >
        {message.severity === 'success' ? (
          <Alert severity="success" variant="filled" sx={{ width: '100%' }} onClose={handleCloseSnakbar}>
            {!message.messageInfo ? 'Bulk scheduling completed successfully!' : message?.messageInfo}
          </Alert>
        ) : (
          <Alert severity="error" variant="filled" sx={{ width: '100%' }} onClose={handleCloseSnakbar}>
            {pendingErrorRules?.length > 0 ? (
              <ul>
                {pendingErrorRules.map((errorRule: any, index: number) => (
                  <li key={index}>
                    {errorRule.workOrderName}: {errorRule.pendingRules.join(', ')}
                  </li>
                ))}
              </ul>
            ) : !message.messageInfo ? (
              ''
            ) : (
              message?.messageInfo
            )}
          </Alert>
        )}
      </Snackbar>
      {openDialog && (
        <Dialog
          open={openDialog}
          className="modalpopup"
          title={'CONFIRM STATUS'}
        // onClose={closeDialog}
        >
          <div>
            <div style={{ padding: '25px 50px' }}>
              <div>
                Do you want to add <>{selectedWorkOrder?.length > 1 ? 'these inspections' : 'this inspection'}</>{' '}
                <strong>{selectedWorkOrder?.map((wo) => wo.workOrderNumber)?.join(', ')}</strong> to{' '}
                <strong>{selectedInspector?.userName}</strong>?
              </div>
            </div>
          </div>
          <DialogActionsBar>
            {isReadOnlyPermission && (
              <Button style={{ maxWidth: '140px' }} size='small' type="button" variant="outlined" onClick={() => setOpenDialog(false)}>
                Cancel
              </Button>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', padding: 2 }} className="  ">
              <button
                className="btn btn-sm btn-primary btn-lightGrey"
                style={{
                  backgroundColor: isReadOnlyPermission ? '#d7dbdd' : '#0000b5',
                  color: isReadOnlyPermission ? 'black' : '#fff',
                  cursor: 'pointer', padding: 2, marginRight: 6
                }}
                disabled={isReadOnlyPermission}
                onClick={closeDialog}
                autoFocus
              >
                No
              </button>

              <button
                className="btn btn-sm btn-primary  btn-delete"
                style={{
                  backgroundColor: isReadOnlyPermission ? '#d7dbdd' : '#0000b5',
                  color: isReadOnlyPermission ? 'black' : '#fff',
                  cursor: 'pointer', padding: 2
                }}
                type="button"
                onClick={handleSubmitConfirm}
              >
                Yes
              </button>
            </div>
          </DialogActionsBar>
        </Dialog>
      )}

      {openDialogForDate && (
        <Dialog
          open={openDialogForDate}
          className="modalpopup"
          title={'CONFIRM STATUS'}
        // onClose={closeDialog}
        >
          <div>
            <div style={{ padding: '25px 50px' }}>
              <div>
                <h4>Please see the below validations</h4>
                <br></br>
                {pendingErrorRules?.length > 0 ? (
                  <>
                    <ul>
                      {pendingErrorRules.map((data: any, index: number) => (
                        <li key={index}>
                          <ul>
                            <span style={{ color: 'orangered' }}>{data?.workOrderName}</span>
                            {data?.pendingRules?.map((errorRule: any) => (
                              <>
                                <li style={{ marginLeft: '25px' }} key={errorRule}>
                                  <span>{errorRule}</span>
                                </li>
                                {errorRule.includes("pre-requisite") && data?.preReqWorkOrders?.length > 0 && (
                                  <dl style={{ paddingLeft: 25 }}>
                                    <dt>Pending Work Orders:</dt>
                                    {data?.preReqWorkOrders?.map((data: any) => <dd key={data}>- {data}</dd>)}
                                  </dl>
                                )}
                              </>
                            ))}
                          </ul>
                        </li>
                      ))}
                    </ul>
                    <br></br>
                    <div>
                      {ishavePermission ? (
                        <h5>
                          You have the permission to schedule without completing the validations. Are you sure you want
                          to continue?
                        </h5>
                      ) : (
                        <h5>You can not schedule without completing the validations.</h5>
                      )}
                    </div>
                  </>
                ) : (
                  ''
                )}
              </div>
              <LoadingIndicator isLoading={isLoading} />
            </div>
          </div>
          <DialogActionsBar>
            <div className=" spaceBetween">
              <button
                style={{ cursor: 'pointer' }}
                className="btn btn-sm btn-default btn-lightGrey"
                onClick={closeValidationDialog}
                autoFocus
              >
                Cancel
              </button>
              {ishavePermission && (
                <button
                  className="btn btn-sm btn-primary  btn-delete"
                  type="button"
                  onClick={handleSumbitConfirm}
                  disabled={isLoading}
                  style={{ cursor: 'pointer' }}
                >
                  Confirm
                </button>
              )}
            </div>
          </DialogActionsBar>
        </Dialog>
      )}
    </div>
  );
};

export default Inspectors;
